import React, { useCallback, useRef, useState } from 'react';
import { Button, Input, Tooltip } from 'antd';
import { AudioOutlined, DeleteOutlined } from '@ant-design/icons';
import useAudioUpload from '../../hooks/useAudioUpload';
import AudioPlaybackControls from '../audio/AudioPlaybackControls';
import AudioRecorder from '../audio_recorder/AudioRecorder';
import UploadProgressDisplay from '../audio/UploadProgressDisplay';

export interface TextAreaWithAudioValue {
	text?: string;
	audioUrl?: string;
}

interface TextAreaWithAudioProps {
	value?: TextAreaWithAudioValue;
	onChange?: (value: TextAreaWithAudioValue) => void;
	userType: string;
	onBusyStateChange?: (isBusy: boolean) => void;
	placeholder?: string;
	rows?: number;
	maxLength?: number;
	style?: React.CSSProperties;
	disabled?: boolean;
}

const TextAreaWithAudio: React.FC<TextAreaWithAudioProps> = ({
	value = {},
	onChange,
	userType,
	onBusyStateChange,
	disabled,
	style,
	...textAreaProps
}) => {
	const [uploading, setUploading] = useState(false);

	const valueRef = useRef(value);
	valueRef.current = value;
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	const handleUploadSuccess = useCallback(
		(fileUrl: string) => {
			if (onChangeRef.current) onChangeRef.current({ ...valueRef.current, audioUrl: fileUrl });
			setUploading(false);
			if (onBusyStateChange) onBusyStateChange(false);
		},
		[onBusyStateChange]
	);

	const handleUploadError = useCallback(() => {
		setUploading(false);
		if (onBusyStateChange) onBusyStateChange(false);
	}, [onBusyStateChange]);

	const upload = useAudioUpload({
		userType: userType,
		onSuccess: handleUploadSuccess,
		onError: handleUploadError,
	});

	const handleSendAudio = useCallback(
		(_blob: Blob, file: File) => {
			setUploading(true);
			if (onBusyStateChange) onBusyStateChange(true);
			upload.uploadAudio(file);
		},
		[upload, onBusyStateChange]
	);

	const hasAudio = !!value.audioUrl;

	const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		if (onChange) onChange({ ...value, text: e.target.value });
	};

	const handleDeleteAudio = () => {
		if (onChange) onChange({ ...value, audioUrl: undefined });
	};

	return (
		<div style={style} className="flex flex-col rounded border border-solid border-grey-5 bg-white">
			{/* Audio strip inside the box */}
			{uploading ? (
				<div className="px-3 pt-3">
					<UploadProgressDisplay progress={upload.uploadProgress} />
				</div>
			) : hasAudio ? (
				<div className="m-3 flex items-center gap-2 rounded-md border-0 bg-grey-4 px-4 py-3">
					<div className="min-w-0 flex-1">
						<AudioPlaybackControls
							src={value.audioUrl || ''}
							activeColor="#757575"
							inactiveColor="#75757580"
							progressLineColor="#757575"
						/>
					</div>
					<Tooltip title="Remove voice note">
						<Button
							shape="circle"
							onClick={handleDeleteAudio}
							icon={<DeleteOutlined />}
							className="shrink-0"
						/>
					</Tooltip>
				</div>
			) : null}

			{/* Text input area */}
			<div className="relative">
				<Input.TextArea
					value={value.text}
					onChange={handleTextChange}
					disabled={disabled}
					bordered={false}
					style={{ paddingRight: 48 }}
					{...textAreaProps}
				/>
				<div className="pointer-events-none absolute bottom-0 right-0 z-[1] flex items-center p-2">
					<div className="pointer-events-auto">
						<AudioRecorder
							containerType="modal"
							onSend={handleSendAudio}
							disabled={disabled || uploading}
							modalTitle="Record Voice Note"
						>
							<Tooltip title={hasAudio ? 'Re-record voice note' : 'Record voice note'}>
								<Button
									disabled={disabled || uploading}
									shape="circle"
									icon={<AudioOutlined />}
									className="flex items-center justify-center bg-grey-4"
								/>
							</Tooltip>
						</AudioRecorder>
					</div>
				</div>
			</div>
		</div>
	);
};

export default TextAreaWithAudio;
